import React, { useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { FiArrowLeft, FiEdit2 } from "react-icons/fi";
import { TailSpin } from "react-loader-spinner";
import toast from "react-hot-toast";

import {
  useCloseSurveyMutation,
  usePublishSurveyMutation,
  useSurveyDetailQuery,
  useSurveySummaryQuery,
} from "../../lib/api";
import SurveyPageShell from "./SurveyPageShell";
import SurveyQuestionsTab from "./SurveyQuestionsTab";
import SurveyResponsesTab from "./SurveyResponsesTab";
import SurveySummaryTab from "./SurveySummaryTab";
import {
  formatOrdinalDate,
  placementLabel,
  surveyErrorMessage,
  surveyStatusBadgeClass,
  surveyStatusLabel,
} from "./surveyTypes";

type TabId = "summary" | "questions" | "responses";

const TABS: { id: TabId; label: string }[] = [
  { id: "summary", label: "Summary" },
  { id: "questions", label: "Questions" },
  { id: "responses", label: "Responses" },
];

const SurveyDetail: React.FC = () => {
  const { surveyId } = useParams<{ surveyId: string }>();
  const navigate = useNavigate();
  const [tab, setTab] = useState<TabId>("summary");

  const {
    data: survey,
    isPending,
    isError,
    error,
    refetch,
  } = useSurveyDetailQuery(surveyId);

  const summaryQuery = useSurveySummaryQuery(surveyId, tab === "summary");

  const publishMutation = usePublishSurveyMutation();
  const closeMutation = useCloseSurveyMutation();

  const status = String(survey?.status ?? "").toUpperCase();
  const busy = publishMutation.isPending || closeMutation.isPending;

  const meta = useMemo(() => {
    if (!survey) return [];
    return [
      { label: "Created", value: formatOrdinalDate(survey.createdAt) },
      {
        label: "Published",
        value: survey.publishedAt ? formatOrdinalDate(survey.publishedAt) : "—",
      },
      {
        label: "Shown in",
        value:
          survey.placements?.length > 0
            ? survey.placements.map(placementLabel).join(", ")
            : "No placement",
      },
      {
        label: "Audience",
        value: String(survey.audience ?? "ALL")
          .replace(/_/g, " ")
          .toLowerCase(),
      },
    ];
  }, [survey]);

  const handlePublish = () => {
    if (!surveyId) return;
    publishMutation.mutate(surveyId, {
      onSuccess: () => toast.success("Survey published"),
      onError: (e) =>
        toast.error(surveyErrorMessage(e, "Could not publish survey.")),
    });
  };

  const handleClose = () => {
    if (!surveyId) return;
    closeMutation.mutate(surveyId, {
      onSuccess: () => toast.success("Survey closed"),
      onError: (e) =>
        toast.error(surveyErrorMessage(e, "Could not close survey.")),
    });
  };

  if (isPending) {
    return (
      <SurveyPageShell>
        <div className="flex justify-center py-24">
          <TailSpin color="#0B75C2" height={40} width={40} />
        </div>
      </SurveyPageShell>
    );
  }

  if (isError || !survey) {
    return (
      <SurveyPageShell>
        <div className="flex flex-col gap-3 rounded-xl border border-red-500/35 bg-red-500/[0.08] px-4 py-4 text-[13px] text-red-100/95 sm:flex-row sm:items-center sm:justify-between">
          <span>
            {surveyErrorMessage(error, "Could not load this survey.")}
          </span>
          <div className="flex shrink-0 gap-2">
            <button
              type="button"
              onClick={() => refetch()}
              className="rounded-lg border border-red-500/40 bg-red-500/20 px-3 py-1.5 text-[12px] font-semibold text-red-200"
            >
              Retry
            </button>
            <button
              type="button"
              onClick={() => navigate("/surveys")}
              className="rounded-lg border border-[#3A4852] bg-[#1C2227] px-3 py-1.5 text-[12px] font-semibold text-[#AEBCC6]"
            >
              Back to surveys
            </button>
          </div>
        </div>
      </SurveyPageShell>
    );
  }

  return (
    <SurveyPageShell>
      <button
        type="button"
        onClick={() => navigate("/surveys")}
        className="mb-4 inline-flex items-center gap-1.5 text-[12px] font-semibold text-[#869AA9] transition hover:text-white"
      >
        <FiArrowLeft className="text-sm" />
        All surveys
      </button>

      <header className="mb-6">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-2.5">
              <h1 className="font-sora text-xl font-semibold tracking-tight text-white">
                {survey.title}
              </h1>
              <span
                className={`inline-flex rounded-full border px-2.5 py-0.5 text-[11px] font-semibold ${surveyStatusBadgeClass(
                  survey.status
                )}`}
              >
                {surveyStatusLabel(survey.status)}
              </span>
            </div>
            {survey.description && (
              <p className="mt-1.5 max-w-2xl text-[13px] leading-relaxed text-[#869AA9]">
                {survey.description}
              </p>
            )}
          </div>

          <div className="flex shrink-0 flex-wrap gap-2">
            {status !== "CLOSED" && (
              <button
                type="button"
                onClick={() => navigate(`/surveys/${survey._id}/edit`)}
                className="inline-flex h-10 items-center gap-1.5 rounded-xl border border-[#3A4852] bg-[#1C2227] px-4 text-[13px] font-semibold text-[#C6D0D7] transition hover:text-white"
              >
                <FiEdit2 className="text-sm" />
                Edit
              </button>
            )}
            {status === "DRAFT" && (
              <button
                type="button"
                disabled={busy || survey.questions.length === 0}
                onClick={handlePublish}
                title={
                  survey.questions.length === 0
                    ? "Add at least one question before publishing"
                    : undefined
                }
                className="inline-flex h-10 items-center justify-center rounded-xl bg-secondary_button px-4 text-[13px] font-semibold text-white shadow-[0_8px_24px_rgba(11,132,203,0.35)] transition hover:brightness-110 disabled:cursor-not-allowed disabled:opacity-50"
              >
                {publishMutation.isPending ? "Publishing…" : "Publish"}
              </button>
            )}
            {(status === "ACTIVE" || status === "SCHEDULED") && (
              <button
                type="button"
                disabled={busy}
                onClick={handleClose}
                className="inline-flex h-10 items-center justify-center rounded-xl border border-[#3A4852] bg-[#1C2227] px-4 text-[13px] font-semibold text-[#C6D0D7] transition hover:border-red-500/50 hover:text-red-300 disabled:opacity-50"
              >
                {closeMutation.isPending ? "Closing…" : "Close survey"}
              </button>
            )}
          </div>
        </div>

        <dl className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-4">
          {meta.map((m) => (
            <div key={m.label}>
              <dt className="text-[11px] text-[#5C6B76]">{m.label}</dt>
              <dd className="mt-0.5 text-[13px] font-medium capitalize text-[#E7EBEE]">
                {m.value}
              </dd>
            </div>
          ))}
        </dl>
      </header>

      {status === "ACTIVE" && (
        <p className="mb-5 rounded-xl border border-[#2C363E] bg-[#1C2227] px-4 py-2.5 text-[12px] text-[#869AA9]">
          This survey is live. Questions and options are locked so
          already-collected answers stay valid — wording and end date can still
          be edited.
        </p>
      )}

      <div
        className="mb-5 flex flex-wrap gap-2 border-b border-[#2C363E]"
        role="tablist"
        aria-label="Survey detail sections"
      >
        {TABS.map((t) => {
          const active = tab === t.id;
          return (
            <button
              key={t.id}
              type="button"
              role="tab"
              aria-selected={active}
              onClick={() => setTab(t.id)}
              className={`-mb-px border-b-2 px-4 py-2.5 text-[14px] font-semibold transition ${
                active
                  ? "border-secondary_button text-white"
                  : "border-transparent text-[#869AA9] hover:text-[#C6D0D7]"
              }`}
            >
              {t.label}
              {t.id === "responses" && survey.stats?.starts > 0 && (
                <span className="ml-1.5 text-[11px] font-medium text-[#5C6B76]">
                  {survey.stats.starts}
                </span>
              )}
            </button>
          );
        })}
      </div>

      {tab === "summary" && (
        <SurveySummaryTab
          summary={summaryQuery.data}
          isPending={summaryQuery.isPending}
          isError={summaryQuery.isError}
          errorMessage={surveyErrorMessage(
            summaryQuery.error,
            "Could not load results."
          )}
          onRetry={() => summaryQuery.refetch()}
        />
      )}
      {tab === "questions" && (
        <SurveyQuestionsTab questions={survey.questions} />
      )}
      {tab === "responses" && surveyId && (
        <SurveyResponsesTab
          surveyId={surveyId}
          questions={survey.questions}
          surveyTitle={survey.title}
        />
      )}
    </SurveyPageShell>
  );
};

export default SurveyDetail;
